/**
 * Administrator form: shared domain model.
 *
 * Pure, framework-free types shared by the `add-administrator` and
 * `edit-administrator` slices. Both features edit the exact same admin form
 * (name, email, role, and since #617 an optional LinkedIn profile URL), so the draft shape, its lifecycle enums, and
 * the validation-errors alias live here; the single genuinely-common,
 * genuinely-stable piece. No React, no Redux, no fetch.
 *
 * This module depends on NEITHER feature (hermeticity) and never reaches into
 * another domain (e.g. the client `list` feature).
 */
import type { AdminRole } from '@/constants/adminRoles';

/** The editable admin form state (identical for Add and Edit). */
export interface AdminDraft {
  firstName: string;
  lastName: string;
  email: string;
  /** Empty string = no role picked yet (the select's pristine value). */
  role: AdminRole | '';
  /** Optional; `''` = none. Sent normalised, `null` when empty (#617). */
  linkedinUrl: string;
}

/** A pristine draft; the starting point for the Add form. */
export const emptyDraft: AdminDraft = {
  firstName: '',
  lastName: '',
  email: '',
  role: '',
  linkedinUrl: '',
};

/** Field-keyed validation messages produced by `validateDraft`. */
export type ValidationErrors = Partial<Record<keyof AdminDraft, string>>;

/** Lifecycle of a save (invitation send / user update). */
export type SaveStatus = 'idle' | 'saving' | 'succeeded' | 'failed';

/** Lifecycle of an initial load (Edit hydrates from the backend). */
export type LoadStatus = 'idle' | 'loading' | 'loaded' | 'failed';
